import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Gauge, ClipboardCheck, Cog, BadgeCheck, Settings, Disc } from "lucide-react";
import SEO from "../components/SEO";
import { BUSINESS, IMAGES } from "../lib/site";
import { OpeningHours } from "../components/layout/Footer";

const HERO_IMG = IMAGES.hero;

const QUICK = [
  { icon: BadgeCheck, title: "MOT TESTING", sub: "Class 4", price: "£45" },
  { icon: Wrench, title: "SERVICING", sub: "Interim & Full Services", price: "From £100" },
  { icon: Gauge, title: "WHEEL ALIGNMENT", sub: "Tracking", price: "£50" },
  { icon: ClipboardCheck, title: "VEHICLE INSPECTION", sub: "Peace of mind", price: "£30" },
];

const SERVICES = [
  {
    title: "MOT CLASS 4", price: "£45", to: "/mot-testing-adlington", cta: "BOOK AN MOT",
    desc: "Professional Class 4 MOT testing at our Adlington workshop. Full retest within 10 working days - £45.",
  },
  {
    title: "TRACKING / WHEEL ALIGNMENT", price: "£50", to: "/booking?service=Wheel Alignment", cta: "BOOK WHEEL ALIGNMENT",
    desc: "Help prevent uneven tyre wear and maintain correct wheel alignment.",
  },
  {
    title: "FULL VEHICLE INSPECTION", price: "£30", to: "/vehicle-inspection", cta: "BOOK INSPECTION",
    desc: "Peace of mind before a long journey, holiday, general check, or when buying or selling a vehicle.",
  },
  {
    title: "INTERIM SERVICE", price: "£100 - £150", to: "/car-servicing-adlington", cta: "BOOK SERVICE",
    desc: "Oil and oil filter change with essential checks. Final pricing varies according to vehicle.",
  },
  {
    title: "FULL SERVICE", price: "£180 - £280", to: "/car-servicing-adlington", cta: "BOOK FULL SERVICE",
    desc: "Full service including full inspection. Final pricing varies according to vehicle.",
  },
];

const WET_BELTS = [
  { title: "FORD 1.0 ECOBOOST WET BELT", price: "£980", engine: "1.0 EcoBoost" },
  { title: "FORD TRANSIT 2.0 WET BELT", price: "£1,050", engine: "2.0 EcoBlue" },
];

const PRICES = [
  ["Class 4 MOT", "£45"],
  ["Full MOT Retest", "£45"],
  ["Tracking / Wheel Alignment", "£50"],
  ["Full Vehicle Inspection", "£30"],
  ["Interim Service", "£100 - £150"],
  ["Ford 1.0 EcoBoost Wet Belt", "£980"],
  ["Ford Transit 2.0 Wet Belt", "£1,050"],
  ["Full Service + Inspection", "£180 - £280"],
];

const WHY = [
  { num: "01", title: "Professional", desc: "Straightforward vehicle servicing and maintenance, carried out properly." },
  { num: "02", title: "Transparent", desc: "Clear pricing with no unnecessary complexity and no surprise extras." },
  { num: "03", title: "Local", desc: "Based in Adlington, Chorley, Lancashire - serving local drivers every day." },
  { num: "04", title: "Convenient", desc: "Easy booking, direct phone contact and local collection/drop-off arrangements where offered." },
];

export default function Home() {
  return (
    <>
      <SEO
        title="PR7 MOT Centre | MOT, Servicing & Ford Wet Belts in Adlington, Chorley"
        description="Class 4 MOT testing from £45, car servicing, wheel alignment £50, vehicle inspections £30 and specialist Ford wet belt replacement in Adlington, Chorley, Lancashire. Call 01257 786 540."
        path="/"
      />

      {/* HERO */}
      <section data-testid="hero-section" className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0 eng-grid" />
        <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-brand/10 blur-3xl" />
        <div className="container-pr7 relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center py-16 lg:py-24">
          <div className="lg:col-span-7 space-y-6 animate-fade-up">
            <p className="eyebrow-light">Margaret House, Huyton Road, Adlington, PR7 4HD</p>
            <h1 className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              PR7 MOT CENTRE
              <span className="block mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold text-sky tracking-tight">
                MOTs, servicing &amp; vehicle care in Adlington
              </span>
            </h1>
            <p className="text-pale/75 text-base sm:text-lg leading-relaxed max-w-xl">
              Professional MOT testing, servicing, inspections and vehicle maintenance from our workshop in Adlington, Lancashire.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link to="/booking?service=MOT" data-testid="hero-book-mot-btn" className="btn-primary">BOOK AN MOT <ArrowRight size={17} /></Link>
              <a href={BUSINESS.phoneHref} data-testid="hero-call-btn" className="btn-outline-light">CALL {BUSINESS.phone}</a>
            </div>
          </div>
          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="absolute -inset-3 border border-sky/20 rounded-lg translate-x-3 translate-y-3" />
            <img
              src={HERO_IMG}
              alt="Technician working on a modern vehicle inside the PR7 MOT Centre workshop"
              className="relative rounded-lg object-cover w-full h-72 sm:h-96 lg:h-[440px] shadow-2xl"
              loading="eager"
            />
            <div className="absolute bottom-4 left-4 bg-navy/90 backdrop-blur border border-deep rounded px-4 py-2.5">
              <p className="font-mono text-[11px] text-sky tracking-wider">CLASS 4 MOT</p>
              <p className="font-heading font-black text-white text-2xl">£45</p>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK SERVICE BAR */}
      <section data-testid="quick-service-bar" className="relative z-20 -mt-0 lg:-mt-10 container-pr7">
        <div className="bg-pale border border-border shadow-xl rounded-lg grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-border">
          {QUICK.map((q) => (
            <Link
              key={q.title}
              to={q.title.includes("MOT") ? "/mot-testing-adlington" : q.title.includes("ALIGNMENT") ? "/booking?service=Wheel Alignment" : q.title.includes("INSPECTION") ? "/vehicle-inspection" : "/car-servicing-adlington"}
              data-testid={`quick-${q.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-center gap-4 p-6 group hover:bg-white transition-colors"
            >
              <q.icon size={30} className="text-brand shrink-0 group-hover:text-sky transition-colors" strokeWidth={1.75} />
              <div>
                <p className="font-heading font-extrabold text-ink text-sm tracking-wide">{q.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{q.sub}</p>
                <p className="font-mono font-semibold text-brand text-sm mt-1">{q.price}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="section-pad bg-white">
        <div className="container-pr7">
          <p className="eyebrow mb-3">What we do</p>
          <h2 className="font-heading font-extrabold text-ink text-3xl lg:text-4xl tracking-tight">Professional vehicle care.</h2>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Straightforward pricing and professional servicing for everyday vehicles and specialist Ford work.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {SERVICES.map((s) => (
              <div
                key={s.title}
                data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group bg-surface border border-border rounded-lg p-7 flex flex-col transition-all duration-200 hover:shadow-lg hover:-translate-y-1 hover:border-brand/30"
              >
                <p className="font-mono font-semibold text-brand text-xl">{s.price}</p>
                <h3 className="font-heading font-extrabold text-ink text-lg mt-2 tracking-wide">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mt-3 flex-1">{s.desc}</p>
                <Link to={s.to} data-testid={`service-cta-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="mt-5 inline-flex items-center gap-2 font-heading font-bold text-sm text-brand group-hover:text-sky tracking-wide transition-colors">
                  {s.cta} <ArrowRight size={15} />
                </Link>
              </div>
            ))}
            {/* Wet belt technical card */}
            <div data-testid="service-card-wet-belts" className="md:col-span-2 lg:col-span-1 relative bg-navy rounded-lg p-7 overflow-hidden flex flex-col">
              <div className="absolute inset-0 eng-grid" />
              <div className="relative flex-1">
                <p className="eyebrow-light mb-3">Specialist Ford Work</p>
                <Settings className="text-sky mb-4" size={28} strokeWidth={1.75} />
                <h3 className="font-heading font-extrabold text-white text-lg tracking-wide">FORD WET BELT REPLACEMENT</h3>
                <p className="text-sm text-pale/70 leading-relaxed mt-3">
                  Wet belt replacement using genuine Ford parts for the 1.0 EcoBoost and Transit 2.0 engines.
                </p>
              </div>
              <Link to="/ford-wet-belt-replacement" data-testid="service-cta-wet-belts" className="relative mt-5 inline-flex items-center gap-2 font-heading font-bold text-sm text-sky hover:text-white tracking-wide transition-colors">
                ENQUIRE NOW <ArrowRight size={15} />
              </Link>
            </div>
          </div>

          {/* Wet belt pricing strip */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {WET_BELTS.map((w) => (
              <div key={w.title} data-testid={`wetbelt-${w.engine.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} className="relative bg-pale border border-brand/20 rounded-lg p-6 flex items-center justify-between gap-4 overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand" />
                <div>
                  <p className="font-heading font-extrabold text-ink tracking-wide">{w.title}</p>
                  <p className="text-sm text-muted-foreground mt-1">Wet belt replacement using genuine Ford parts.</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono font-semibold text-brand text-2xl">{w.price}</p>
                  <Link to="/ford-wet-belt-replacement" className="text-xs font-heading font-bold text-brand hover:text-sky tracking-wide">ENQUIRE NOW &rarr;</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section data-testid="why-section" className="section-pad bg-navy relative overflow-hidden">
        <div className="absolute inset-0 eng-grid" />
        <div className="container-pr7 relative">
          <p className="eyebrow-light mb-3">Why PR7 MOT Centre</p>
          <h2 className="font-heading font-extrabold text-white text-3xl lg:text-4xl tracking-tight">A garage you can rely on.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {WHY.map((w) => (
              <div key={w.num} data-testid={`why-${w.title.toLowerCase()}`} className="border-t-2 border-brand/50 pt-5">
                <p className="font-mono text-sky text-sm font-semibold">{w.num}</p>
                <h3 className="font-heading font-bold text-white text-xl mt-2">{w.title}</h3>
                <p className="text-sm text-pale/65 leading-relaxed mt-2">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section data-testid="pricing-section" className="section-pad bg-surface">
        <div className="container-pr7 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-3">Transparent pricing</p>
            <h2 className="font-heading font-extrabold text-ink text-3xl lg:text-4xl tracking-tight">Our current prices</h2>
            <div className="mt-8 bg-white border border-border rounded-lg overflow-hidden shadow-sm">
              <table data-testid="pricing-table" className="w-full text-sm">
                <thead>
                  <tr className="bg-navy text-white">
                    <th className="text-left font-heading font-bold tracking-wider text-xs uppercase px-5 py-3.5">Service</th>
                    <th className="text-right font-heading font-bold tracking-wider text-xs uppercase px-5 py-3.5">Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {PRICES.map(([s, p]) => (
                    <tr key={s} className="hover:bg-pale/60 transition-colors">
                      <td className="px-5 py-3.5 text-ink font-medium">{s}</td>
                      <td className="px-5 py-3.5 text-right font-mono font-semibold text-brand">{p}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
              Prices shown are guide prices. Final pricing may vary depending on vehicle, specification and parts required. Please contact us for confirmation.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-navy rounded-lg p-8 h-full flex flex-col justify-between relative overflow-hidden">
              <div className="absolute inset-0 eng-grid" />
              <div className="relative">
                <h3 className="font-heading font-extrabold text-white text-2xl tracking-tight">Ready to book?</h3>
                <p className="text-pale/70 text-sm leading-relaxed mt-3">
                  Request your MOT, service or inspection online in under a minute, or call the workshop directly.
                </p>
                <div className="mt-6">
                  <p className="font-heading font-bold text-white text-xs uppercase tracking-wider mb-3">Opening Hours</p>
                  <OpeningHours dark />
                </div>
              </div>
              <div className="relative flex flex-col gap-3 mt-8">
                <Link to="/booking" data-testid="pricing-book-btn" className="btn-primary justify-center">REQUEST BOOKING <ArrowRight size={16} /></Link>
                <a href={BUSINESS.phoneHref} data-testid="pricing-call-btn" className="btn-outline-light justify-center">CALL {BUSINESS.phone}</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WORKSHOP IMAGE BAND */}
      <section className="relative h-72 sm:h-96 overflow-hidden">
        <img src={IMAGES.workshop} alt="Inside the PR7 MOT Centre workshop in Adlington" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="relative container-pr7 h-full flex flex-col justify-center">
          <h2 className="font-heading font-extrabold text-white text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-2xl">
            Modern vehicles. Honest work. Proper prices.
          </h2>
          <Link to="/about-us" data-testid="band-about-link" className="mt-5 inline-flex items-center gap-2 font-heading font-bold text-sky hover:text-white text-sm tracking-wide transition-colors w-fit">
            ABOUT THE GARAGE <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </>
  );
}
